/**
 * CampoDeDimension.tsx — pedir una clasificación analítica sin adivinar.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ POR QUÉ EXISTE: UN CAMPO LIBRE CONTRA UN VOCABULARIO CERRADO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `department` y `stream` tienen vocabulario cerrado desde la `0023`, y hasta el
 * 11 de setiembre **ninguna pantalla lo ofrecía**. Se escribía a mano, y la base
 * contestaba `SB100 · ese valor no está en el vocabulario de esa dimensión` sin
 * que existiera un solo sitio en el producto donde ver cuáles son los siete
 * valores válidos.
 *
 * Lo destapó escribir «administracion» en la caminata, que es exactamente lo que
 * escribiría cualquiera: es una palabra correcta en español y no está en la
 * lista —la lista dice `ga`—.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ Y SIGUE SIENDO UN CAMPO LIBRE DONDE LA DIMENSIÓN ES ABIERTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `customer` y `project` no se cerraron a propósito —cerrar `customer` obligaría
 * a dar de alta un cliente antes de poder facturarle—. Así que esto pregunta al
 * catálogo y dibuja lo que corresponda. Quién es abierta y quién cerrada lo
 * decide la TABLA, no este archivo: el día que `modalidad` exista con sus dos
 * valores, aparece como desplegable sin tocar una línea de aquí.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ DOS FORMAS, Y NO ES DECORACIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `bloque` es un campo de formulario —`/banco`, `/activos`—. `linea` es la fila
 * apretada que el formulario de asiento pone debajo de cada línea, con su clase
 * de aviso cuando falta.
 *
 * Están las dos acá y no una copia en cada pantalla porque **lo que hay que
 * mantener de acuerdo no es el aspecto: es la decisión** —desplegable o texto
 * libre— y ésa se toma una vez. Una copia del `input` en el formulario de
 * asiento es exactamente lo que dejó a ese formulario tres meses pidiendo a
 * ciegas la única dimensión que no se puede adivinar.
 */
import { useCatalogo, useValoresDe } from '../nucleo/catalogo.tsx';
import { useIdioma } from '../nucleo/idioma.tsx';

export function CampoDeDimension(
  { dimension, valor, alCambiar, variante = 'bloque' }: {
    readonly dimension: string;
    readonly valor: string;
    readonly alCambiar: (v: string) => void;
    readonly variante?: 'bloque' | 'linea';
  },
) {
  const { t } = useIdioma();
  /* ▶▶ `supplier` y `customer` NO son texto libre, aunque el `vocabulario` los
     deje abiertos (0023). Son una lista cerrada: la base los rechaza si no están
     en `contrapartes` con la bandera correcta —`es_proveedor` o `es_cliente`—. Lo
     cobró César al asentar: le pedía digitarlos y rebotaba con «esa contraparte
     no existe». Acá se traen del catálogo y se ofrecen como desplegable, con
     NOMBRE + código: el código solo es ilegible, y pedirlo a mano es exactamente
     lo que hacía fallar el asiento. */
  const { contrapartes } = useCatalogo();
  const esContraparte = dimension === 'supplier' || dimension === 'customer';
  const papel = dimension === 'supplier' ? 'es_proveedor' : 'es_cliente';
  const opciones = esContraparte
    ? contrapartes.filter((cp) => cp[papel as 'es_proveedor' | 'es_cliente'] && cp.activa)
    : null;

  const valores = useValoresDe(dimension);
  const enLinea = variante === 'linea';

  // ▶ El aviso de «falta» va SOLO en la variante de línea, y no es un descuido:
  // en el formulario de asiento la fila convive con otras nueve y hace falta que
  // salte; en un formulario de alta el botón ya está apagado mientras falte, que
  // dice lo mismo sin pintar de rojo algo que la persona todavía no ha llenado.
  const falta = enLinea && valor.trim() === '';

  /* ══════════════════════════════════════════════════════════════════════
     ▶▶ SE VISTE SIEMPRE, Y ANTES SE VESTÍA SÓLO EN LÍNEA
     ══════════════════════════════════════════════════════════════════════

     Decía `className={enLinea ? '…' : undefined}`, así que en la variante de
     bloque salía **un desplegable crudo del navegador**: 18 px y la esquina
     viva, en medio de un formulario donde todo lo demás mide 40 px con la
     esquina redonda. Es exactamente lo que el guardián 18 existe para impedir.

     ▶ Y esta frase dice «desplegable» y no el nombre del elemento a propósito:
     el guardián 18 se puso rojo sobre este mismo comentario. Un comentario de
     bloque conserva sus líneas de en medio cuando se quitan los comentarios
     por línea, así que nombrar la trampa dentro del comentario la dispara. Es
     la tercera vez que esta casa lo aprende — está escrito en el módulo de
     alcanzabilidad como límite conocido, y la prosa se mide como si fuera
     código.

     ▶▶ Y la versión anterior de esta nota escribía la marca de cierre de un
     comentario de bloque para nombrarla, **y cerró este comentario**. El
     archivo dejó de compilar. Nombrar un símbolo escribiéndolo es la misma
     trampa un piso más abajo: acá tampoco se escribe.

     Funcionaba en `Activos` porque allá cae dentro de un contenedor que viste
     por descendencia. O sea que el componente **sólo se veía bien según quién
     fuera su padre** — un componente que depende de eso es una trampa puesta
     para el siguiente que lo use. Lo cobró la caminata al usarlo en la factura
     de venta.

     ▶ Y el guardián 18 lo dejó pasar teniéndolo delante: le bastaba con que
     EXISTIERA un `className=`, sin mirar que la expresión valiera `undefined`.
     Eso también se arregló. */
  const control = opciones !== null ? (
    <select
      className={`select${enLinea ? ` input--sm${falta ? ' input--warn' : ''}` : ''}`}
      value={valor}
      onChange={(e) => alCambiar(e.target.value)}
    >
      <option value="">—</option>
      {opciones.map((cp) => (
        <option key={cp.codigo} value={cp.codigo}>{cp.nombre}</option>
      ))}
    </select>
  ) : valores === null ? (
    <input
      className={`input${enLinea ? ` input--sm${falta ? ' input--warn' : ''}` : ''}`}
      value={valor}
      onChange={(e) => alCambiar(e.target.value)}
    />
  ) : (
    // `input--sm` y no un `select--sm` inventado: la clase ya existe y es un
    // modificador de TAMAÑO, no de elemento. Una clase nueva que hiciera lo
    // mismo son dos sitios donde cambiar el alto de un campo.
    //
    // ▶ El nombre humano del valor sale de `voc.{dimension}.{valor}` en el
    // diccionario; cuando no existe traducción, cae al código crudo. Lo que
    // viaja al asiento sigue siendo el código, no el rótulo — el rótulo es
    // solo para el ojo del que elige.
    <select
      className={`select${enLinea ? ` input--sm${falta ? ' input--warn' : ''}` : ''}`}
      value={valor}
      onChange={(e) => alCambiar(e.target.value)}
    >
      <option value="">—</option>
      {valores.map((v) => {
        const rotulo = t(`voc.${dimension}.${v}` as never);
        const mostrar = rotulo === `voc.${dimension}.${v}` ? v : rotulo;
        return <option key={v} value={v}>{mostrar}</option>;
      })}
    </select>
  );

  if (enLinea) {
    return (
      <label className="field field--inline">
        <span className="field__label">{t(`cls.${dimension}` as never)}</span>
        {control}
      </label>
    );
  }

  return (
    <label className="field">
      <span className="field__label">{t(`cls.${dimension}` as never)}</span>
      {control}
    </label>
  );
}
